<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="light">
                <ion-buttons slot="start">
                    <ion-button color="primary" @click="cancel">
                        <ion-icon slot="icon-only" :icon="close"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <div class="flex">
                    <ion-label class="ml-2">
                        {{ title }}
                    </ion-label>
                </div>

                <ion-buttons slot="end">
                    <ion-button color="primary" @click="toggleMenu()">
                        <mdicon slot="start" name="menu-open"></mdicon>
                    </ion-button>
                </ion-buttons>
            </ion-toolbar>
        </ion-header>

        <ion-content color="custom-white" id="assistant-content">
            <!-- Main content area -->
            <div id="main-content" class="ion-page">
                <no-data icon="cloud-off-outline" v-if="!$store.getters.online">
                    <template #info>
                        <p class="no-data-title">Geen internetverbinding</p>
                        <p class="no-data-subtitle">
                            Ohmi heeft een internetverbinding nodig om te werken. Zorg ervoor dat je verbonden bent met
                            het
                            internet.
                        </p>
                    </template>
                </no-data>

                <no-data :icon="icon" v-else-if="conversation.messages.length == 0">
                    <template #info>
                        <h2>{{ title }}</h2>
                        <p class="no-data-subtitle text-dark">
                            <template v-if="mode != 'create_mode'">
                                Ohmi is een AI-assistent die je kan helpen met het beantwoorden van vragen, specifiek
                                gericht op ondersteuning bij elektrotechnische inspecties.
                            </template>
                            <template v-else>
                                Met bewerken met AI kun je bouwdelen en componenten automatisch laten invoeren. Stel een
                                vraag en deze assistent zal je helpen met het invullen van de gegevens.
                            </template>
                        </p>
                        <p> <small class="text-dark">
                                Door gebruik te maken van deze service, vrijwaart de gebruiker de aanbieder van deze
                                service van alle juridische risico's en aansprakelijkheden die voortvloeien uit het
                                gebruik van door AI gegenereerde inhoud. De gebruiker erkent en accepteert dat de
                                verantwoordelijkheid voor het verifiëren van de juistheid en legaliteit van de
                                gegenereerde inhoud volledig bij de gebruiker ligt.
                            </small></p>
                    </template>
                </no-data>

                <div v-else ref="commentslist" class="comments-list">
                    <div class="comments-list-content mt-4" ref="comment_content">
                        <TransitionGroup name="level">

                            <div v-for="(comment, index) in conversation.messages" :key="index" class="comment-wrapper" :class="getCommentAlignment(comment)">

                                <!-- we show round bubble with "Jij" or "Ohmi" -->
                                <div class="comment-avatar-medium" v-if="comment.type == 'USER'">
                                    <div class="comment-avatar-text">Jij</div>
                                </div>
                                <div class="comment-avatar-primary" v-else>
                                    <mdicon name="robot-outline" class="comment-avatar-icon"></mdicon>
                                </div>

                                <div class="w-100 ml-3">
                                    <div class="comment flex" :class="getCommentColor(comment)">
                                        <!-- small copy to clipboard button -->
                                        <div class="comment-content text-dark" v-html="markdown(comment.content)"></div>

                                    </div>

                                    <div class="comment-created-label">
                                        {{ $fuzzyDate(comment.created_at, true) }} door {{
                        comment.type == 'USER' ? 'jou' : 'Ohmi'
                    }}
                                    </div>
                                </div>
                            </div>
                        </TransitionGroup>

                        <Transition name="level">
                            <div v-if="currently_typing_name" class="comment-typing">
                                <ion-spinner color="medium" name="dots"></ion-spinner>
                                <span class="ml-2">{{ currently_typing_name }} is aan het typen</span>
                            </div>
                        </Transition>
                    </div>
                </div>
            </div>
        </ion-content>

        <ion-footer>
            <div>
                <div class="comment-input-wrapper flex" v-if="$store.getters.online">
                    <div ref="comment_textarea" :contenteditable="contenteditable" @input="updateText" class="editable-div" :data-placeholder="placeholder" @keydown.enter.prevent="handleEnterKey">


                    </div>
                    <small class="disclaimer">
                        Ohmi kan er naast zitten. Verifieer antwoorden altijd zelf.
                    </small>

                    <!-- send button -->
                    <div @click="createComment()" class="comment-button" v-ripple>
                        <mdicon v-if="!loading" class="icon" name="send"></mdicon>
                        <ion-spinner v-else color="light" name="dots"></ion-spinner>
                    </div>

                </div>


            </div>
        </ion-footer>

        <!-- Sidebar for selecting conversations -->
        <ion-menu ref="conv_menu" type="push" side="end" content-id="assistant-content" :swipe="true">
            <ion-header>
                <ion-toolbar color-light>

                    <ion-buttons slot="start">
                        <ion-button color="primary" @click="$refs.conv_menu.$el.close()">
                            <mdicon name="menu-close"></mdicon>
                        </ion-button>
                    </ion-buttons>

                    <ion-title color="primary">Gesprekken</ion-title>

                    <ion-buttons slot="end">
                        <ion-button color="primary" @click="newConversation()">
                            <mdicon name="chat-plus-outline"></mdicon>
                        </ion-button>
                    </ion-buttons>
                </ion-toolbar>
            </ion-header>

            <ion-content>
                <ion-list>
                    <ion-item-group v-for="group in parsedConversations" :key="group.title">
                        <div class="subtle-divider">
                            {{ group.title }}
                        </div>
                        <div class="conversation-row" :class="isSelectedConversation(conversation) ? 'selected-row' : null" v-for="conversation in group.conversations" :key="conversation.uuid" @click="selectConversation(conversation)">
                            <ion-label class="max-width conversation-title">

                                {{ conversation.title }}

                            </ion-label>
                            <ion-button color="medium" size="small" fill="clear" slot="end" @click.stop="deleteConversation(conversation)">
                                <mdicon class="small-icon" name="trash-can-outline"></mdicon>
                            </ion-button>
                        </div>
                    </ion-item-group>
                </ion-list>

                <no-data icon="chat-outline" v-if="conversations.length == 0">
                    <template #info>
                        <p class="no-data-title">Er zijn nog geen gesprekken</p>
                        <p class="no-data-subtitle">
                            Start een nieuw gesprek met Ohmi door een vraag te stellen.
                        </p>
                    </template>
                </no-data>
            </ion-content>
        </ion-menu>


    </ion-page>
</template>
<script>
import { add, close } from 'ionicons/icons';
import { alertController, modalController, IonSpinner, IonMenu, IonItemGroup, IonAvatar } from '@ionic/vue';
import ohmi from '../assets/ohmi.svg'
import { Clipboard } from '@capacitor/clipboard';
import NoData from '../components/NoData.vue';
import MarkdownIt from "markdown-it";
import { icon } from 'leaflet';

export default {
    name: 'assistant-ohmi',
    components: {
        IonSpinner,
        IonMenu,
        NoData,
        IonItemGroup,
    },
    props: {
        mode: {
            type: String,
            default: 'conversation_mode'
        }
    },
    data() {
        return {
            ohmi,
            currently_typing_name: null,
            loading: true,
            add,
            close,
            new_comment: '',
            conversations: [],
            typing_timeout: null,
            show_info: false,
            new_comment_added: false,
            contenteditable: true,
            conversation: {
                messages: []
            }
        }
    },
    created() {
        this.getConversations()
    },
    mounted() {
        this.$refs.comment_textarea.classList.add("placeholder");
    },
    methods: {
        copyToClipboard(text) {
            Clipboard.write({
                string: text
            });
        },
        cancel() {
            modalController.dismiss();
        },
        handleEnterKey(event) {
            event.preventDefault();
            document.execCommand('insertHTML', false, '<br><br>');
        },
        getCommentAlignment(comment) {
            if (comment.type == 'COMMENT') {
                if (comment.created_by.id && comment.created_by.id == this.$store.getters.getUser.id) {
                    return 'flex-end'
                } else if (comment.created_by == this.$store.getters.getUser.id) {
                    return 'flex-end'
                }
                return 'flex-start';
            }
        },
        getCommentColor(comment) {
            if (comment.type == 'USER') {
                return 'comment-primary ';
            }
            return 'comment-medium';
        },
        canDelete(comment) {
            return this.$store.getters.getUser.id == comment.created_by.id && comment.type == 'COMMENT'
        },
        getDate(date) {
            return new Date(date).toLocaleString('nl-NL', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            })
        },
        createComment() {
            if (!this.new_comment) return;

            this.loading = true;
            this.currently_typing_name = 'Ohmi';
            this.contenteditable = false;

            // Gebruik platte tekst voor de commentaar
            let text = this.new_comment;

            this.new_comment = null;
            this.$refs.comment_textarea.innerHTML = '';
            this.$refs.comment_textarea.focus();

            let new_comment = {
                content: text,
                type: "USER",
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            }

            this.conversation.messages.push(new_comment);

            this.scrollToBottomSmoothly();

            this.$axios.post(localStorage.getItem('api_url') + '/openai-chat', {
                chat: text,
                conversation_id: this.conversation.id,
                inspection_id: this.$store.getters.getInspectionId,
                mode: this.mode
            }).then((response) => {
                this.conversation = response.data.conversation;
                this.currently_typing_name = null;
                this.contenteditable = true;
                this.getConversations();
                this.scrollToBottomSmoothly();
            }).catch((error) => {
                this.$toast.error(error.response.data.message, 'danger');
                this.loading = false;
                this.currently_typing_name = null;
                this.contenteditable = true;
            });
        },
        scrollToBottomSmoothly() {
            setTimeout(() => {
                let comments = document.querySelectorAll('.comment-wrapper');
                if (comments.length > 0) {
                    comments[comments.length - 1].scrollIntoView({
                        behavior: 'smooth'
                    });
                }
            }, 300);
        },
        toggleMenu() {
            this.$refs.conv_menu.$el.toggle();
        },
        newConversation() {
            this.conversation = {
                title: 'Nieuw gesprek',
                messages: []
            }
            this.$refs.conv_menu.$el.close();
        },
        getConversations() {
            this.$axios.get(localStorage.getItem('api_url') + '/openai-chat'
            ).then((response) => {
                this.conversations = response.data.conversations;
                this.loading = false;
            }).catch((error) => {
                this.$toast.error(error.response.data.message, 'danger')
                this.loading = false;
            })
        },
        selectConversation(conversation) {
            this.conversation = conversation;
            this.$refs.conv_menu.$el.close();
            this.scrollToBottomSmoothly();
        },
        deleteConversation(conversation) {
            alertController.create({
                mode: 'ios',
                header: 'Verwijderen',
                message: 'Weet je zeker dat je dit gesprek wilt verwijderen?',
                buttons: [
                    {
                        text: 'Annuleren',
                        cssClass: 'secondary',
                        handler: () => {
                            console.log('Confirm Cancel: blah');
                        },
                        role: 'cancel'
                    }, {
                        text: 'Verwijderen',
                        handler: () => {
                            this.deleteConfirmed(conversation)
                        },
                        role: 'destructive'
                    }
                ]
            }).then(alert => {
                alert.present();
            });

        },
        deleteConfirmed(conversation) {
            this.$axios.delete(localStorage.getItem('api_url') + '/openai-chat/delete/' + conversation.id
            ).then(() => {
                this.getConversations()
            }).catch((error) => {
                this.$toast.error(error.response.data.message, 'danger')
            })
        },
        updateText(e) {
            // Maak een tijdelijke element aan om HTML te strippen
            let tempDiv = document.createElement('div');
            tempDiv.innerHTML = e.target.innerHTML;
            // Haal alleen de tekstinhoud eruit en behoud nieuwe regels en spaties
            let text = tempDiv.innerText || tempDiv.textContent || "";
            // Vervang nieuwe regels door <br> tags en spaties door &nbsp;
            let formattedText = text.replace(/\n/g, '<br>').replace(/ {2}/g, ' &nbsp;');
            // Update de content van het contenteditable veld met geformatteerde tekst
            e.target.innerHTML = formattedText;
            // Verplaats de cursor naar het einde van de tekst als er inhoud is
            if (text.length > 0) {
                let range = document.createRange();
                let sel = window.getSelection();
                range.setStart(e.target.childNodes[e.target.childNodes.length - 1], e.target.childNodes[e.target.childNodes.length - 1].length);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
            }
            // Update de new_comment data eigenschap
            this.new_comment = text;
            this.$refs.comment_textarea.classList.toggle("placeholder", !this.new_comment);
        },
        reset() {
            this.$axios.delete(localStorage.getItem('api_url') + '/openai-chat/delete'
            ).then(() => {
                this.getConversations()
            }).catch((error) => {
                this.$toast.error(error.response.data.message, 'danger')
            })
        },
        markdown(text) {
            return new MarkdownIt({
                html: true,
                linkify: true,
                typographer: true,
                breaks: true,
                xhtmlOut: true
            }).render(text)
        },
        isSelectedConversation(conversation) {
            return this.conversation.id == conversation.id
        },
    },
    computed: {
        icon() {
            if (this.mode == 'create_mode') {
                return 'creation'
            }

            return 'robot-outline'
        },
        title() {
            // if mode is create_mode 
            if (this.mode == 'create_mode') {
                return 'Bewerken met AI'
            }

            return 'AI assistent Ohmi (beta)'
        },
        parsedConversations() {
            let conversations = this.conversations;
            let parsedConversations = [];

            // Dates for filtering
            let today = new Date();
            let yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            let lastWeek = new Date(today);
            lastWeek.setDate(lastWeek.getDate() - 7);
            let lastMonth = new Date(today);
            lastMonth.setMonth(lastMonth.getMonth() - 1);

            // Helper function to check if a date is the same day
            const isSameDay = (date1, date2) => {
                return date1.getFullYear() === date2.getFullYear() &&
                    date1.getMonth() === date2.getMonth() &&
                    date1.getDate() === date2.getDate();
            };

            // Group conversations by date
            let todayConversations = [];
            let yesterdayConversations = [];
            let lastWeekConversations = [];
            let lastMonthConversations = [];
            let olderConversations = [];

            conversations.forEach(conversation => {
                let createdAt = new Date(conversation.created_at);
                if (isSameDay(createdAt, today)) {
                    todayConversations.push(conversation);
                } else if (isSameDay(createdAt, yesterday)) {
                    yesterdayConversations.push(conversation);
                } else if (createdAt > lastWeek) {
                    lastWeekConversations.push(conversation);
                } else if (createdAt > lastMonth) {
                    lastMonthConversations.push(conversation);
                } else {
                    olderConversations.push(conversation);
                }
            });

            // sort on created_at
            todayConversations.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            yesterdayConversations.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            lastWeekConversations.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            lastMonthConversations.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            olderConversations.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            // Add to parsedConversations
            if (todayConversations.length > 0) {
                parsedConversations.push({ title: 'Vandaag', conversations: todayConversations });
            }
            if (yesterdayConversations.length > 0) {
                parsedConversations.push({ title: 'Gisteren', conversations: yesterdayConversations });
            }
            if (lastWeekConversations.length > 0) {
                parsedConversations.push({ title: 'Vorige week', conversations: lastWeekConversations });
            }
            if (lastMonthConversations.length > 0) {
                parsedConversations.push({ title: 'Vorige maand', conversations: lastMonthConversations });
            }
            if (olderConversations.length > 0) {
                parsedConversations.push({ title: 'Ouder', conversations: olderConversations });
            }

            return parsedConversations;
        },
        placeholder() {
            return this.new_comment === '' || this.new_comment == null ? "Wat is jouw vraag?" : null;
        },
        getHeight() {
            if (this.new_comment) {
                let height = this.$refs.comment_textarea.clientHeight;
                let scrollHeight = this.$refs.comment_textarea.scrollHeight;
                if (scrollHeight > height) {
                    return scrollHeight + 'px'
                }
            }
            return '40px'
        }
    }
}
</script>


<style scoped>
.comment h1,
.comment h2,
.comment h3,
.comment h4,
.comment h5,
.comment h6 {
    font-size: 13pt;
    margin-top: 10px;
    margin-bottom: 10px;
}

.comment-content p {
    font-size: 11pt;
    margin-top: 5px;
    margin-bottom: 5px;
}

.subtle-divider {
    font-size: 13px;
    color: var(--ion-color-medium);
    font-weight: 600;
    padding-top: 0;
    padding-bottom: 0;
    background-color: unset;
    margin-bottom: 0px;
    margin-left: 17px;
    position: relative;
    margin-bottom: -3px;
    z-index: 9999999;
    margin-top: 25px;
}

.selected-row {
    border-radius: 10px;
    background-color: var(--ion-color-primary-tint);
    color: var(--ion-color-primary);
}

.conversation-row {
    margin: 5px;
    padding: 3px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--ion-color-medium-tint);
    padding-left: 13px;
}
</style>
